/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useToast } from "@/shared/hooks/use-toast";
import { useTranslation } from "react-i18next";
import AuthService from "../services/AuthService";
import { UserRole } from "@/shared/types/user-role";
import SignUpService from "../services/SignUpService";
import { analyticsService } from "@/shared/services/analytics";
import { API_CONFIG } from "@/infrastructure/config/api";
import { logger } from "@/infrastructure/lib/logger";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes — token expires in 1h

type Provider = "google" | "azure" | "email";

type AuthContextType = {
  user: User | null;
  signIn: (provider: Provider | "email", email?: string, password?: string, redirectQuery?: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  refreshSession: () => Promise<boolean>;
  isAuthenticated: boolean;
  isAuthenticating: boolean; // Added new state to track authentication verification process
};

export interface User {
  id: string;
  aud?: string;
  role?: UserRole;
  email?: string;
  email_confirmed_at?: string;
  phone?: string;
  confirmed_at?: string;
  last_sign_in_at?: string;
  app_metadata?: {
    provider?: string;
    [key: string]: any;
  };
  user_metadata?: {
    full_name?: string;
    role?: UserRole;
    organization_id?: string;
    status?: string;
    avatar_url?: string;
    [key: string]: any;
  };
  identities?: any[];
  created_at?: string;
  updated_at?: string;
  // Propriedades customizadas para facilitar acesso
  fullName?: string;
  organizationId?: string | null;
  status?: string;
  avatarUrl?: string;
  productIds?: string[];
  isSuperAdmin?: boolean;
  // Vem flat no spread do /auth/me v2; ausente no legacy (tratar como completed)
  onboardingStatus?: string | null;
  // Campo para determinar tipo de validação na deleção de conta
  requiresPasswordForDelete?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function buildUserObject(userData: Record<string, any>, userMetadata: Record<string, any>): User {
  return {
    ...userData,
    user_metadata: userMetadata,
    app_metadata: {},
    aud: 'authenticated',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    confirmed_at: new Date().toISOString(),
    last_sign_in_at: new Date().toISOString(),
    fullName: userData.fullName || '',
    organizationId: userData.organizationId || null,
    status: userData.status || 'active',
    avatarUrl: userData.avatarUrl || '',
    isSuperAdmin: userData.isSuperAdmin ?? false,
    requiresPasswordForDelete: userData.requiresPasswordForDelete ?? true,
  } as unknown as User;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true); // Start with authentication check in progress
  const { toast } = useToast();
  const { t } = useTranslation();

  const inactivityTimeoutRef = useRef<NodeJS.Timeout>();
  const refreshIntervalRef = useRef<NodeJS.Timeout>();
  // AuthService usa singleton pattern
  const authService = useRef(AuthService.getInstance());
  const isRefreshingSessionRef = useRef(false);

  // Declarações antecipadas para evitar erro de referência antes da declaração
  const signOutRef = useRef<() => Promise<void>>();

  // Funções de gerenciamento de tempo e atividade  
  const resetInactivityTimeout = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    if (isAuthenticated) {
      inactivityTimeoutRef.current = setTimeout(() => {
        if (signOutRef.current) {
          signOutRef.current();
          toast({
            title: t("auth.session.expired"),
            description: t("auth.session.expiredDescription"),
            variant: "destructive",
          });
        }
      }, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated, toast, t]);

  const handleActivity = useCallback(() => {
    resetInactivityTimeout();
  }, [resetInactivityTimeout]);

  // Função para renovar o token
  const refreshSession = useCallback(async () => {
    if (isRefreshingSessionRef.current) return false;
    isRefreshingSessionRef.current = true;
    try {
      console.log('Refreshing session...');

      // Use Auth Service to refresh the token (cookies will handle the token)
      const response = await authService.current.refreshSession();

      if (!response.success || !response.data) {
        // 401 é definitivo (refresh token revogado/expirado): manter o interval
        // vivo só gera retry infinito de hora em hora contra um token morto.
        // Erros transientes (rede, 5xx, timeout) seguem mantendo a sessão.
        if (response.status === 401) {
          logger.warn('Refresh token rejected (401) — signing out');
          if (signOutRef.current) {
            signOutRef.current();
            toast({
              title: t("auth.session.expired"),
              description: t("auth.session.expiredDescription"),
              variant: "destructive",
            });
          }
          return false;
        }
        console.warn('No session returned from refresh (transient), keeping session');
        return false;
      }

      // Update user state with the returned user data
      // Garantir que o objeto atenda aos requisitos do tipo User
      const apiUser = response.data.user;

      // O backend (/auth/refresh) retorna o ApiUser flat (fullName, avatarUrl…), SEM
      // user_metadata. A UI lê de user.user_metadata.full_name — então é obrigatório
      // reconstruir user_metadata aqui, igual ao initializeAuth. Sem isso, após um
      // refreshSession (ex.: salvar Display Name) user_metadata fica undefined e a tela
      // exibe "Not set" até dar reload manual na página.
      const role = (apiUser.role as UserRole) || UserRole.USER;
      const userMetadata = {
        role,
        avatar_url: apiUser.avatarUrl || '',
        organization_id: apiUser.organizationId || '',
        full_name: apiUser.fullName || '',
        status: apiUser.status || 'active',
      };

      const userData = buildUserObject(
        {
          ...apiUser,
          role,
          isSuperAdmin: apiUser.isSuperAdmin ?? (apiUser.role === UserRole.SUPERADMIN),
          requiresPasswordForDelete: apiUser.requiresPasswordForDelete ?? true,
        },
        userMetadata
      );

      setUser(userData);
      setIsAuthenticated(true);

      console.log('Session refreshed successfully');
      return true;
    } catch (error) {
      console.warn('Refresh failed (transient error), keeping session:', error);
      return false;
    } finally {
      isRefreshingSessionRef.current = false;
    }
  }, [toast, t]);

  const setupRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    if (isAuthenticated) {
      refreshIntervalRef.current = setInterval(() => {
        refreshSession();
      }, TOKEN_REFRESH_INTERVAL);
    }
  }, [isAuthenticated, refreshSession]);

  const signIn = async (provider: "google" | "azure" | "email", email?: string, password?: string, redirectQuery?: string) => {
    // Só ativar loading geral para email/password
    // OAuth usa loading específico nos botões
    if (provider === "email") {
      setLoading(true);
    }
    let isOnboardingRedirect = false;

    try {
      let authResponse;

      if (provider === "email" && email && password) {
        // Use Auth Service for email/password login
        authResponse = await authService.current.login(email, password);

        if (!authResponse.success || !authResponse.data) {
          throw new Error(authResponse.message || "Invalid sign in method");
        }

        // Create a user object from the response
        const userData = authResponse.data.user;

        // 🔄 Buscar dados completos via validate-token (organizationId, isSuperAdmin, etc.)
        try {
          const validateResponse = await authService.current.validateSession(true);
          if (validateResponse.success && validateResponse.data?.user) {
            const validated = validateResponse.data.user;
            userData.organizationId = validated.organizationId || userData.organizationId;
            userData.fullName = validated.fullName || userData.fullName;
            userData.avatarUrl = validated.avatarUrl || userData.avatarUrl;
            userData.status = validated.status || userData.status;
            userData.isSuperAdmin = validated.isSuperAdmin ?? false;
            userData.role = validated.role || userData.role;
          }
        } catch (error) {
          console.error('[AuthContext] Erro ao buscar dados via validate-token:', error);
        }

        // Garante que a role seja a mesma do backend (que foi verificada pelo AuthService.login)
        const userRole = (userData.role as UserRole) || UserRole.USER;

        // Cria o objeto com a role correta e metadados necessários para o tipo User
        const newUser: User = {
          id: userData.id,
          email: userData.email || '',
          user_metadata: {
            full_name: userData.fullName || '',
            role: userRole, // Usa a role verificada do backend
            organization_id: userData.organizationId || '',
            status: userData.status || '',
            avatar_url: userData.avatarUrl || ''
          },
          app_metadata: {},
          aud: 'authenticated',
          created_at: new Date().toISOString(),
          confirmed_at: new Date().toISOString(),
          last_sign_in_at: new Date().toISOString(),
          role: userRole, // Garante que a role principal também esteja correta
          updated_at: new Date().toISOString(),
          // Propriedades customizadas para facilitar acesso
          fullName: userData.fullName || '',
          organizationId: userData.organizationId || null,
          status: userData.status || 'active',
          avatarUrl: userData.avatarUrl || '',
          // productIds: userData.productIds || []
          isSuperAdmin: userData.isSuperAdmin ?? false,
          requiresPasswordForDelete: userData.requiresPasswordForDelete ?? true, // Default true para retrocompatibilidade
        };

        // Salvar o avatar_url também no localStorage para garantir carregamento imediato
        const avatarUrl = userData.avatarUrl;
        if (avatarUrl) {
          console.log('[AuthContext] Salvando avatar_url no localStorage:', avatarUrl);
          localStorage.setItem('cached_avatar_url', avatarUrl);
          localStorage.setItem('avatar_timestamp', Date.now().toString());
        }

        setUser(newUser);
        setIsAuthenticated(true);

        resetInactivityTimeout();

        // Track successful login
        analyticsService.trackUserLogin();
        
        // Check onboarding status after successful login
        try {
          const onboardingResponse = await SignUpService.checkOnboardingStatus();
          if (onboardingResponse.success && onboardingResponse.data?.onboarding_required) {
            console.log('[AuthContext] Onboarding required, redirecting immediately...');
            isOnboardingRedirect = true;
            // Redirecionar IMEDIATAMENTE sem delay para evitar flash da tela principal
            window.location.href = '/onboarding';
            // Lançar exceção especial para sinalizar que não deve continuar
            throw new Error('ONBOARDING_REQUIRED');
          }
        } catch (onboardingError: any) {
          // Se for o erro especial de onboarding, propagar para não mostrar toast
          if (onboardingError.message === 'ONBOARDING_REQUIRED') {
            throw onboardingError;
          }
          console.error('[AuthContext] Error checking onboarding status:', onboardingError);
        }
      } else if (provider === "google" || provider === "azure") {
        // Use Auth Service for OAuth login
        const redirectUrl = window.location.origin + '/oauth/callback' + (redirectQuery || '');
        const oauthResponse = await authService.current.getOAuthUrl(provider, redirectUrl);

        if (!oauthResponse.success || !oauthResponse.data?.url) {
          throw new Error(oauthResponse.message || "Failed to get OAuth URL");
        }

        // Redirect to OAuth provider
        window.location.href = oauthResponse.data.url;
        return; // OAuth will redirect, no need to continue
      } else {
        throw new Error("Invalid sign in method");
      }
    } catch (error: any) {
      console.error("Sign in error:", error);

      // Não mostrar toast se for redirecionamento para onboarding
      if (error.message !== 'ONBOARDING_REQUIRED') {
        toast({
          title: t("auth.signInFailed"),
          description: resolveApiErrorMessage(error, t),
          variant: "destructive",
        });
      }
    } finally {
      // Não resetar loading se for redirecionamento para onboarding (para manter spinner)
      if (!isOnboardingRedirect) {
        setLoading(false);
      }
    }
  };

  // Definição das funções principais
  const signOut = useCallback(async () => {
    try {
      setLoading(true);

      // Use Auth Service for logout (cookies will be cleared by the server)
      await authService.current.logout();

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Clear authentication state
      setUser(null);
      setIsAuthenticated(false);

      // Track logout event
      analyticsService.trackUserLogout();

      // Display success message
      toast({
        title: t("auth.signOutSuccess"),
        description: t("auth.signOutSuccessDescription"),
        variant: "default",
      });

      // Redirect to login page
      window.location.href = '/login';
    } catch (error: any) {
      console.error("Sign out error:", error);

      toast({
        title: t("auth.signOutFailed"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [authService, setIsAuthenticated, setLoading, setUser, toast, t]);

  const initializeAuth = async () => {
    // Verificar se estamos em uma página pública (não requer autenticação)
    const isOAuthConsentPage = window.location.pathname.startsWith('/oauth/consent');
    const isPublicPage = window.location.pathname.startsWith('/login') ||
      window.location.pathname.startsWith('/auth') ||
      (window.location.pathname.startsWith('/oauth') && !isOAuthConsentPage) ||
      window.location.pathname.startsWith('/register') ||
      window.location.pathname.startsWith('/signup') ||
      window.location.pathname.startsWith('/recover-password') ||
      window.location.pathname.startsWith('/unsubscribe');

    /**
     * Safety-net: processa convite social pendente que OAuthCallback não consumiu.
     * Cenário: GoTrue proxy não redirecionou para /oauth/callback com tokens no hash,
     * mas a sessão OAuth foi estabelecida. Ao detectar o pending invite_token no localStorage,
     * consome o convite usando o access_token armazenado.
     * Retorna true se o convite foi aceito (página será recarregada).
     */
    const processPendingSocialInvite = async (): Promise<boolean> => {
      try {
        const pendingToken = localStorage.getItem('prodgy_pending_invite_token');
        const pendingTs = localStorage.getItem('prodgy_pending_invite_ts');
        if (!pendingToken || !pendingTs) return false;

        const elapsed = Date.now() - parseInt(pendingTs, 10);
        if (elapsed > 15 * 60 * 1000) {
          // Expirado (> 15 min) — limpar e continuar fluxo normal
          localStorage.removeItem('prodgy_pending_invite_token');
          localStorage.removeItem('prodgy_pending_invite_ts');
          sessionStorage.removeItem('invite_token');
          return false;
        }

        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return false;

        logger.info('[AuthContext] Pending social invite found, accepting...');

        const response = await fetch(`${API_CONFIG.API_URL}/invites/accept-via-social`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ inviteToken: pendingToken, accessToken })
        });

        const data = await response.json();

        // Limpar tokens independente do resultado (evita loop)
        localStorage.removeItem('prodgy_pending_invite_token');
        localStorage.removeItem('prodgy_pending_invite_ts');
        sessionStorage.removeItem('invite_token');

        if (response.ok && data.success) {
          logger.info('[AuthContext] Social invite accepted via safety-net, reloading...');
          window.location.replace('/');
          return true;
        }

        logger.warn('[AuthContext] Social invite acceptance failed', { status: response.status });
        return false;
      } catch (err) {
        // Não bloquear o fluxo de autenticação em caso de erro
        logger.error('[AuthContext] Error processing pending social invite', { error: err instanceof Error ? err.message : String(err) });
        localStorage.removeItem('prodgy_pending_invite_token');
        localStorage.removeItem('prodgy_pending_invite_ts');
        sessionStorage.removeItem('invite_token');
        return false;
      }
    };

    try {
      setLoading(true);
      setIsAuthenticating(true); // Start authentication check

      // Check if we're on the OAuth callback page - let OAuthCallback component handle it
      if (window.location.pathname === '/oauth/callback') {
        console.log('[AuthContext] 🔐 On OAuth callback page, skipping auth initialization');
        console.log('[AuthContext] 🔐 Letting OAuthCallback component process tokens from hash');
        
        // Don't try to validate or refresh - OAuthCallback will handle everything
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }

      // Se estamos em uma página pública, não tentar validar autenticação
      if (isPublicPage) {
        console.log('[AuthContext] Em página pública, pulando validação de autenticação');
        setLoading(false);
        setIsAuthenticating(false);
        return;
      }

      // Validate the token with the server
      console.log('[AuthContext] Validando token atual com servidor...');
      // Use silent mode to avoid unnecessary 401 error logs during initialization
      const response = await authService.current.validateSession(true);

      if (response.success && response.data?.user) {
        // Criar uma cópia do objeto de usuário para manipular com segurança
        const userData = {
          ...response.data.user,
          // Garantir que mantenha a role definida no JWT
          role: (response.data.user.role as UserRole) || UserRole.USER
        };

        // Criar os metadados do usuário usando os dados disponíveis
        const userMetadata = {
          role: userData.role as UserRole || UserRole.USER,
          avatar_url: userData.avatarUrl || '',
          organization_id: userData.organizationId || '',
          full_name: userData.fullName || '',
          status: userData.status || 'active'
        };

        // Verificar se a role nos metadados corresponde à role do JWT
        if (userData.role && (!userMetadata.role || userMetadata.role !== userData.role)) {
          console.log('[AuthContext] Atualizando role nos metadados do usuário:', userData.role);
          // Atualizar os metadados de forma segura
          (userData as any).user_metadata = {
            ...userMetadata,
            role: userData.role
          };
        }

        // Debug: verificar se requiresPasswordForDelete está vindo do backend
        console.log('[AuthContext] initializeAuth - userData.requiresPasswordForDelete:', userData.requiresPasswordForDelete);
        
        const userObject = buildUserObject(userData, userMetadata);

        console.log('[AuthContext] initializeAuth - Mapped userObject.requiresPasswordForDelete:', userObject.requiresPasswordForDelete);

        // Atualizar estado com os dados do JWT
        setUser(userObject);
        setIsAuthenticated(true);
        resetInactivityTimeout();
        // setupTokenValidationInterval removido - não mais necessário
        
        // Safety-net: processar convite social pendente que OAuthCallback não conseguiu consumir
        // (ex: GoTrue proxy não redirecionou corretamente para /oauth/callback)
        if (await processPendingSocialInvite()) {
          return; // Invite aceito — página vai recarregar com dados corretos
        }

        // Verificar onboarding status antes de finalizar autenticação
        // Mas SOMENTE se não estivermos já na página de onboarding (evita loop)
        if (window.location.pathname !== '/onboarding') {
          try {
            const onboardingResponse = await SignUpService.checkOnboardingStatus();
            if (onboardingResponse.success && onboardingResponse.data?.onboarding_required) {
              console.log('[AuthContext] Onboarding required during init, redirecting immediately...');
              // Redirecionar para onboarding se necessário
              window.location.href = '/onboarding';
              return; // Não finalizar autenticação, deixar spinner ativo
            }
          } catch (onboardingError) {
            console.error('[AuthContext] Error checking onboarding status during init:', onboardingError);
          }
        } else {
          console.log('[AuthContext] Already on onboarding page, skipping redirect check');
        }
        
        setIsAuthenticating(false); // End authentication check after successful validation
        return;
      } else {
        console.warn('[AuthContext] Validação de token falhou, tentando renovar token...');

        // Tentar renovar o token antes de redirecionar
        console.log('[AuthContext] Tentando renovação proativa de token...');
        const refreshResult = await authService.current.refreshSession();

        if (refreshResult.success && refreshResult.data?.user) {
          console.log('[AuthContext] Renovação de token bem-sucedida');
          console.log('[AuthContext] Dados do usuário após renovação:', refreshResult.data.user);

          // Criar uma cópia do objeto de usuário para manipular com segurança
          const userData = {
            ...refreshResult.data.user,
            // Garantir que mantenha a role definida no JWT
            role: (refreshResult.data.user.role as UserRole) || UserRole.USER
          };

          // Criar metadados do usuário
          const userMetadata = {
            role: userData.role as UserRole || UserRole.USER,
            avatar_url: userData.avatarUrl || '',
            organization_id: userData.organizationId || '',
            full_name: userData.fullName || '',
            status: userData.status || 'active'
          };

          const userObject = buildUserObject(userData, userMetadata);

          // Atualizar estado com os dados do JWT
          setUser(userObject);
          setIsAuthenticated(true);
          resetInactivityTimeout();
          // setupTokenValidationInterval removido - não mais necessário

          // Safety-net: processar convite social pendente
          if (await processPendingSocialInvite()) {
            return;
          }

          // Verificar onboarding status antes de finalizar autenticação
          // Mas SOMENTE se não estivermos já na página de onboarding (evita loop)
          if (window.location.pathname !== '/onboarding') {
            try {
              const onboardingResponse = await SignUpService.checkOnboardingStatus();
              if (onboardingResponse.success && onboardingResponse.data?.onboarding_required) {
                console.log('[AuthContext] Onboarding required after refresh, redirecting immediately...');
                // Redirecionar para onboarding se necessário
                window.location.href = '/onboarding';
                return; // Não finalizar autenticação, deixar spinner ativo
              }
            } catch (onboardingError) {
              console.error('[AuthContext] Error checking onboarding status after refresh:', onboardingError);
            }
          } else {
            console.log('[AuthContext] Already on onboarding page, skipping redirect check');
          }
          
          setIsAuthenticating(false);

          console.log('[AuthContext] Autenticação restaurada com sucesso após renovação de token');
          return;
        }

        console.warn('[AuthContext] Renovação de token falhou, redirecionando para login');
        // Clear any stale state
        setUser(null);
        setIsAuthenticated(false);

        // Only redirect to login if we're not already on a public page
        // Preserve current URL to return to it after login
        if (!isPublicPage) {
          console.log('[AuthContext] Redirecionando para página de login...');
          // Save current URL to return to it after login
          sessionStorage.setItem('prodgy_redirect_after_login', window.location.pathname + window.location.search);
          window.location.href = '/login';
        } else {
          console.log('[AuthContext] Já estamos em uma página pública, não redirecionando');
        }

        setIsAuthenticating(false); // End authentication check after failed validation
      }
    } catch (err) {
      console.error('[AuthContext] Erro ao validar token:', err);

      // Tentar renovar o token antes de redirecionar, mesmo em caso de erro
      try {
        console.log('[AuthContext] Tentando renovar token após erro de validação...');
        const refreshResult = await authService.current.refreshSession();

        if (refreshResult.success && refreshResult.data?.user) {
          console.log('[AuthContext] Renovação de token bem-sucedida após erro');

          // Criar uma cópia do objeto de usuário para manipular com segurança
          const userData = {
            ...refreshResult.data.user,
            // Garantir que mantenha a role definida no JWT
            role: (refreshResult.data.user.role as UserRole) || UserRole.USER
          };

          // Criar metadados do usuário
          const userMetadata = {
            role: userData.role as UserRole || UserRole.USER,
            avatar_url: userData.avatarUrl || '',
            organization_id: userData.organizationId || '',
            full_name: userData.fullName || '',
            status: userData.status || 'active'
          };

          const userObject = buildUserObject(userData, userMetadata);

          // Atualizar estado com os dados do JWT
          setUser(userObject);
          setIsAuthenticated(true);
          resetInactivityTimeout();
          // setupTokenValidationInterval removido - não mais necessário

          // Safety-net: processar convite social pendente
          if (await processPendingSocialInvite()) {
            return;
          }

          setIsAuthenticating(false);

          console.log('[AuthContext] Autenticação restaurada com sucesso após erro');
          return;
        }
      } catch (refreshError) {
        console.error('[AuthContext] Erro ao tentar renovar token:', refreshError);
      }

      // Se chegou aqui, nem a validação nem o refresh funcionaram
      // Clear any stale state
      setUser(null);
      setIsAuthenticated(false);
      setIsAuthenticating(false); // End authentication check on error

      // Only redirect to login if we're not already on a public page
      // (isPublicPage já foi declarado no início da função)
      if (!isPublicPage) {
        console.log('[AuthContext] Redirecionando para página de login devido a erro de autenticação');
        // Save current URL to return to it after login
        sessionStorage.setItem('prodgy_redirect_after_login', window.location.pathname + window.location.search);
        window.location.href = '/login';
      } else {
        console.log('[AuthContext] Já estamos em uma página pública, não redirecionando');
      }
    } finally {
      setLoading(false);
    }
  };

  // Atribuir a função signOut à referência para uso em outras funções
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  // Initialize auth state on mount - run only once
  useEffect(() => {
    // Função para reinicializar a autenticação quando necessário
    initializeAuth();

    // Set up activity listeners
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      // Clean up activity listeners
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);

      // Clear intervals and timeouts
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  useEffect(() => {
    if (isAuthenticated) {
      setupRefreshInterval();
    }
  }, [isAuthenticated, setupRefreshInterval]);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signOut,
        loading,
        refreshSession,
        isAuthenticated,
        isAuthenticating
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
